import type { JSX } from 'react';

import { CONDITIONS, STATS, STEPS, MORE_THAN_VISITS, WHY } from './content.js';
import { Band, BandHeading } from './sections.js';

/** "Why work with a dietitian through NourishedRx" + the pastel condition grid. */
export function WhyNourishedRx(): JSX.Element {
  return (
    <Band>
      {/* The header CTA links here, matching the marketing site's "Why NourishedRx" anchor. */}
      <div id="why-nourishedrx" className="flex flex-col gap-10 scroll-mt-24">
        <div className="mx-auto flex max-w-[52rem] flex-col gap-4 text-center">
          <BandHeading lead={WHY.heading} />
          <p className="nrx-prose text-[var(--ink)]">{WHY.body}</p>
        </div>
        <h3 className="text-center text-lg font-semibold italic text-[var(--coral)]">
          {WHY.conditionsHeading}
        </h3>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CONDITIONS.map((c) => (
            <li
              key={c.label}
              className="flex flex-col items-center justify-center gap-4 rounded-[var(--radius)] p-8 text-center"
              style={{ backgroundColor: c.tint }}
            >
              {/*
               * Per-icon width, height left to follow the icon's own aspect ratio.
               *
               * These six icons have six different aspect ratios (128x150 through 65x128). The
               * original markup forced them all into a 48x48 square with explicit width AND height
               * and no object-fit, which stretched every non-square one — `icon-maintain-muscle`,
               * at 0.51 aspect, rendered nearly twice as wide as it should. Fitting them to a
               * shared box instead removes the distortion, but no single box suits both the tall
               * narrow icon and the squat wide ones: whichever dimension you cap, half of them come
               * out visually undersized.
               *
               * So each carries its own width (see CONDITIONS), matching what the marketing site
               * draws. The 96px slot is what keeps the labels aligned across the grid — it is a
               * layout row, not a constraint on the image.
               */}
              <span className="flex h-24 items-center justify-center">
                <img src={c.icon} alt={c.alt} style={{ width: c.iconWidth }} className="h-auto" />
              </span>
              <span className="text-lg font-semibold text-[var(--forest)]">{c.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </Band>
  );
}

/** "Start getting support in just a few steps" — photo plus the four numbered steps. */
export function HowItWorks(): JSX.Element {
  return (
    <Band>
      <div className="flex flex-col gap-10">
        <BandHeading
          lead="Start getting support "
          accent="in just a few steps"
          className="text-center"
        />
        {/* items-center: the steps block is centred against the photo, as on the marketing site,
            rather than hanging from its top edge. */}
        <div className="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <img
            src="/images/photo-steps.webp"
            alt="Woman at home smiling while holding a glass of water"
            className="h-auto w-full rounded-[var(--radius-lg)] object-cover"
            width={720}
            height={890}
          />
          <ol className="flex flex-col gap-6">
            {STEPS.map((step, i) => (
              <li key={step.title} className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                <span
                  className="h-fit w-fit shrink-0 rounded-md px-3 py-1 text-xs font-bold tracking-wide text-[var(--forest)]"
                  style={{ backgroundColor: step.pill }}
                >
                  STEP {i + 1}
                </span>
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-[var(--forest)]">{step.title}</span>
                  <p className="nrx-prose text-[var(--ink)]">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Band>
  );
}

/** "More than just nutrition visits" + the four published outcome figures. */
export function MoreThanVisits(): JSX.Element {
  return (
    <Band>
      <div className="flex flex-col gap-10">
        <div className="grid items-center gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <img
            src="/images/photo-more-than-visits.webp"
            alt="Smiling woman eating healthy vegetables"
            className="h-auto w-full rounded-[var(--radius-lg)] object-cover"
            width={460}
            height={460}
          />
          <div className="flex flex-col gap-4">
            <BandHeading lead={MORE_THAN_VISITS.heading} />
            {MORE_THAN_VISITS.paragraphs.map((p) => (
              <p key={p.slice(0, 24)} className="nrx-prose text-[var(--ink)]">
                {p}
              </p>
            ))}
          </div>
        </div>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="nrx-card flex flex-col items-center gap-1 rounded-[var(--radius)] px-5 py-7 text-center"
            >
              <dt className="text-3xl font-bold text-[var(--coral)]">{s.value}</dt>
              <dd className="text-sm leading-snug text-[var(--ink)]">{s.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Band>
  );
}
